import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { EASE } from "@/lib/scroll";

const TABS = [
  { id: "all", label: "All", testid: "gallery-tab-all" },
  { id: "outdoors", label: "Deck & Outdoors", testid: "gallery-tab-outdoors" },
  { id: "interiors", label: "Interiors", testid: "gallery-tab-interiors" },
  { id: "beach", label: "The Beach", testid: "gallery-tab-beach" },
  { id: "village", label: "Around VB", testid: "gallery-tab-village" },
];

const GROUPS = [
  { id: "outdoors", label: "The Cottage & Grounds" },
  { id: "interiors", label: "Inside the Cottage" },
  { id: "beach", label: "The Beach" },
  { id: "village", label: "Around Victoria Beach" },
];

const IMAGES = [
  {
    src: "/photos/cottage-exterior.webp",
    caption: "The Cottage",
    spec: "100+ years under the birches",
    story:
      "Over a hundred years old and modernized with care, the cottage sits tucked under the birches, just 300 steps from the sand at Connaught Beach.",
    cat: "outdoors",
    span: "lg:col-span-2 lg:row-span-2",
  },
  {
    src: "/photos/living-1.webp",
    caption: "The Octagon Room",
    spec: "The front living room · eight sides of cozy",
    story:
      "It really is in the shape of an octagon. Plenty of seating, a desk for any paperwork or writing, and a TV ready to connect your favourite streaming services. Books and games are here too.",
    cat: "interiors",
    span: "lg:col-span-2",
  },
  {
    src: "/photos/bedroom-1.webp",
    caption: "Master Bedroom",
    spec: "Built-in queen nook · crisp linen",
    story:
      "A great queen-sized bed, plenty of storage for clothing, and two windows that open to let the summer breeze flow through. A ceiling fan keeps the air moving.",
    cat: "interiors",
    span: "",
  },
  {
    src: "/photos/bunk-room.webp",
    caption: "The Bunk Room",
    spec: "Pipe-frame bunks · the old trunk",
    story:
      "A twin bed on the lower bunk and a single on top, with an oversized amount of headroom on the bottom bunk. Plenty of storage, and a private room for guests.",
    cat: "interiors",
    span: "",
  },
  {
    src: "/photos/fireplace-1.webp",
    caption: "Fireplace Room",
    spec: "Brick wood fireplace · firewood at the ready",
    story:
      "The wonderful bump out is made for a summer nap in the sun, or room for two to sleep. A window AC can take the bite out of a hot day, the wood fireplace can warm up a cold one, and the ceiling fan spreads the air all around.",
    cat: "interiors",
    span: "lg:col-span-2",
  },
  {
    src: "/photos/kitchen-2.webp",
    caption: "The IKEA Kitchen",
    spec: "Full galley · stove, microwave & big fridge",
    story:
      "A stocked kitchen with a coffee maker, air fryer, pots and pans, a full-sized fridge and freezer, and a microwave.",
    cat: "interiors",
    span: "",
  },
  {
    src: "/photos/kitchen-nook.webp",
    caption: "Eat-In Kitchen",
    spec: "The morning table · ferns outside",
    cat: "interiors",
    span: "",
  },
  {
    src: "/photos/living-2.webp",
    caption: "The Octagon Room",
    spec: "The red sofa corner",
    story:
      "It really is in the shape of an octagon. Plenty of seating, a desk for any paperwork or writing, and a TV ready to connect your favourite streaming services. Books and games are here too.",
    cat: "interiors",
    span: "",
  },
  {
    src: "/photos/living-3.webp",
    caption: "Movie Nights",
    spec: "TV, books & board games",
    cat: "interiors",
    span: "",
  },
  {
    src: "/photos/fireplace-2.webp",
    caption: "The Bump Out",
    spec: "A sunny window seat for slow afternoons",
    cat: "interiors",
    span: "",
  },
  {
    src: "/photos/bedroom-2.webp",
    caption: "Master Bedroom",
    spec: "Wardrobes & garden light",
    story:
      "A great queen-sized bed, plenty of storage for clothing, and two windows that open to let the summer breeze flow through. A ceiling fan keeps the air moving.",
    cat: "interiors",
    span: "",
  },
  {
    src: "/photos/bathroom-1.webp",
    caption: "The Washroom",
    spec: "Remodeled · full laundry",
    story:
      "A nice sized washroom, governed by the old cottage adage: 'if it's yellow...'",
    cat: "interiors",
    span: "",
  },
  {
    src: "/photos/living-4.webp",
    caption: "The Writing Desk",
    spec: "'Living the dream at the lake'",
    cat: "interiors",
    span: "",
  },
  {
    src: "/photos/fireplace-3.webp",
    caption: "Firewood & Quilts",
    spec: "Ready for a cold evening",
    cat: "interiors",
    span: "",
  },
  {
    src: "/photos/bathroom-2.webp",
    caption: "Washroom Vanity",
    spec: "Bright & simply done",
    story: "A fully renovated bathroom with a washer and dryer.",
    cat: "interiors",
    span: "",
  },
  {
    src: "/photos/garden-path.webp",
    caption: "The Garden Path",
    spec: "Stone walls · ferns & birch shade",
    cat: "outdoors",
    span: "lg:col-span-2",
  },
  {
    src: "/photos/porch-star.webp",
    caption: "The Front Step",
    spec: "Screen door · yellow bench · the welcome star",
    cat: "outdoors",
    span: "",
  },
  {
    src: "/photos/screen-room-day.webp",
    caption: "The Screen Room",
    spec: "Wicker mornings · garden views",
    story:
      "There is a great sofa here for summer naps, a dining room table to enjoy a meal, and an upright freezer to store your frozen goods. A ceiling fan keeps the air moving.",
    cat: "outdoors",
    span: "",
  },
  {
    src: "/photos/screen-room-night.webp",
    caption: "The Screen Room at Night",
    spec: "Twinkle lights till late",
    story:
      "The perfect spot for an evening of cards or a nightcap, under the twinkle lights.",
    cat: "outdoors",
    span: "",
  },
  {
    src: "/photos/deck-1.webp",
    caption: "The Back Deck",
    spec: "Patio dining & the red BBQ",
    story:
      "Plenty of room for larger gatherings, with a BBQ and an outdoor dining table. It is surrounded by greenery on every side, so your time on the deck stays private.",
    cat: "outdoors",
    span: "lg:col-span-2",
  },
  {
    src: "/photos/deck-2.webp",
    caption: "Deck Life",
    spec: "Yellow chairs in the birch shade",
    story:
      "Spend an afternoon with a cold beverage and a good book on the lounge, with burgers on the BBQ.",
    cat: "outdoors",
    span: "",
  },
  {
    src: "/photos/deck-3.webp",
    caption: "The Garden Corner",
    spec: "Potted colour all summer",
    cat: "outdoors",
    span: "",
  },
  {
    src: "/photos/outdoor-shower.webp",
    caption: "Outdoor Shower",
    spec: "Private & secluded · under sun or stars",
    story:
      "A private shower behind a locked door, under the sun or the stars, with plenty of room to change and shower.",
    cat: "outdoors",
    span: "",
  },
  {
    src: "/photos/deck-lights.webp",
    caption: "The Back Garden",
    spec: "String lights after dark",
    story:
      "A secluded path leads to the back deck, and motion sensor lights light the path at night.",
    cat: "outdoors",
    span: "lg:col-span-2",
  },
  {
    src: "/photos/beach-sunset.webp",
    caption: "Connaught Beach",
    spec: "Sunset on the sand · 300 steps away",
    cat: "beach",
    span: "lg:row-span-2",
  },
  {
    src: "/photos/beach-bluebird.webp",
    caption: "Bluebird Day",
    spec: "Calm water · bright sky",
    cat: "beach",
    span: "lg:row-span-2",
  },
  {
    src: "/photos/sunset-cove.webp",
    caption: "The Rocky Shore",
    spec: "Evenings at the point",
    cat: "beach",
    span: "",
  },
  {
    src: "/photos/beach-dusk-point.webp",
    caption: "The Point at Dusk",
    spec: "Driftwood forts & pastel skies",
    cat: "beach",
    span: "",
  },
  {
    src: "/photos/sunset-garden.webp",
    caption: "The Lakefront at Dusk",
    spec: "Wildflowers on the evening walk",
    cat: "beach",
    span: "lg:col-span-2",
  },
  {
    src: "/photos/beach-curve.webp",
    caption: "Connaught Beach",
    spec: "The long sandy curve · 300 steps away",
    cat: "beach",
    span: "lg:col-span-2",
  },
  {
    src: "/photos/beach-pelicans.webp",
    caption: "Pelican Rocks",
    spec: "The locals, sunbathing",
    story:
      "A bike ride down to the pier can bring you face to face with large flocks of birds, pelicans included.",
    cat: "beach",
    span: "lg:col-span-2 lg:row-span-2",
  },
  {
    src: "/photos/sunset-breakwater.webp",
    caption: "Afterglow",
    spec: "The 11th largest lake in the world",
    story: "The sunsets here are wonderful.",
    cat: "beach",
    span: "lg:col-span-2",
  },
  {
    src: "/photos/welcome-arch.webp",
    caption: "The Welcome Arch",
    spec: "Every arrival starts here",
    cat: "village",
    span: "lg:col-span-2",
  },
  {
    src: "/photos/vra-biking.webp",
    caption: "Car-Free Streets",
    spec: "Bikes, wagons & bare feet",
    story: "The entire community is wonderfully bike friendly.",
    cat: "village",
    span: "lg:row-span-2",
  },
  {
    src: "/photos/hiking-trail.webp",
    caption: "The Trails",
    spec: "Groomed paths through the pines",
    story: "Numerous hiking trails to enjoy, winding through the pines.",
    cat: "village",
    span: "lg:row-span-2",
  },
  {
    src: "/photos/tennis-courts.webp",
    caption: "Tennis Morning",
    spec: "Bike up · play · repeat",
    story:
      "Wander over to play tennis, take part in the tennis programs, join the numerous summer tournaments, or watch a match while enjoying treats from the bakery 100 feet away.",
    cat: "village",
    span: "lg:col-span-2",
  },
  {
    src: "/photos/bike-gathering.webp",
    caption: "The Gathering",
    spec: "Common sight at parties in Victoria Beach",
    cat: "village",
    span: "",
  },
  {
    src: "/photos/tennis-lessons.webp",
    caption: "Lessons in Session",
    spec: "Programs July 1 to mid-August",
    cat: "village",
    span: "",
  },
  {
    src: "/photos/village-green.webp",
    caption: "Saturday at the Green",
    spec: "Pavilion crowds & a hundred bikes",
    story: "Numerous events are held here all through the summer.",
    cat: "village",
    span: "lg:col-span-2",
  },
  {
    src: "/photos/village-playground.webp",
    caption: "The Playground",
    spec: "Where the kids disappear to",
    story: "A large play structure for the kiddies to enjoy.",
    cat: "village",
    span: "lg:col-span-2",
  },
  {
    src: "/photos/aurora.webp",
    caption: "Northern Lights",
    spec: "Early & late season · from the deck or the beach",
    cat: "village",
    span: "lg:col-span-2",
  },
];

export default function Gallery() {
  const [tab, setTab] = useState("all");
  const [active, setActive] = useState(null);

  const filtered = tab === "all" ? IMAGES : IMAGES.filter((img) => img.cat === tab);

  const renderTile = (img) => (
    <motion.button
      layout
      key={img.src}
      data-testid={`gallery-image-spotlight-trigger-${IMAGES.indexOf(img) + 1}`}
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.5, ease: EASE }}
      onClick={() => setActive({ ...img, index: IMAGES.indexOf(img) })}
      className={`group relative overflow-hidden rounded-xl ${img.span}`}
    >
      <img
        src={img.src}
        alt={img.caption}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
      <div className="absolute bottom-0 left-0 translate-y-2 p-5 text-left opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
        <p className="font-serif text-xl text-[#F7F4EF]">{img.caption}</p>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#F7F4EF]/75">
          {img.spec}
        </p>
      </div>
    </motion.button>
  );

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="bg-[#EFECE6] py-24 sm:py-36"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-12 flex flex-wrap items-end justify-between gap-8"
        >
          <div className="max-w-xl">
            <div className="mb-6 flex items-center gap-4">
              <span className="h-px w-12 bg-[#C8634B]" />
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C8634B]">
                The Gallery
              </p>
            </div>
            <h2 className="font-serif text-3xl leading-snug tracking-tight text-[#1E3323] sm:text-4xl lg:text-5xl">
              Every corner, <span className="italic text-[#C8634B]">in the light.</span>
            </h2>
          </div>

          <div className="flex flex-wrap gap-3" data-testid="gallery-tabs">
            {TABS.map((t) => {
              const count =
                t.id === "all"
                  ? IMAGES.length
                  : IMAGES.filter((img) => img.cat === t.id).length;
              return (
                <button
                  key={t.id}
                  data-testid={t.testid}
                  onClick={() => setTab(t.id)}
                  className={`rounded-full px-5 py-3 font-sans text-xs font-semibold uppercase tracking-[0.12em] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md ${
                    tab === t.id
                      ? "bg-[#C8634B] text-[#F7F4EF] shadow-md"
                      : "border border-[#1E3323]/25 bg-white text-[#1E3323] shadow-sm hover:border-[#C8634B]"
                  }`}
                >
                  {t.label}
                  <span
                    className={`ml-2 rounded-full px-1.5 py-0.5 font-mono text-[10px] ${
                      tab === t.id ? "bg-white/20" : "bg-[#1E3323]/10"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </motion.div>

        {tab === "all" ? (
          GROUPS.map((g) => {
            const imgs = IMAGES.filter((img) => img.cat === g.id);
            return (
              <div
                key={g.id}
                data-testid={`gallery-group-${g.id}`}
                className="mb-16 last:mb-0"
              >
                <div className="mb-6 flex items-baseline gap-4">
                  <h3 className="font-serif text-2xl italic text-[#1E3323] sm:text-3xl">
                    {g.label}
                  </h3>
                  <span className="h-px flex-1 bg-[#1E3323]/15" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#767970]">
                    {imgs.length} photos
                  </span>
                </div>
                <div className="grid auto-rows-[230px] grid-flow-dense grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {imgs.map((img) => renderTile(img))}
                </div>
              </div>
            );
          })
        ) : (
          <motion.div
            layout
            className="grid auto-rows-[230px] grid-flow-dense grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
          >
            <AnimatePresence mode="popLayout">
              {filtered.map((img) => renderTile(img))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-spotlight-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm sm:p-8"
          >
            <motion.figure
              initial={{ scale: 0.92, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.45, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[92vh] w-full max-w-4xl overflow-y-auto bg-[#F7F4EF] p-3 shadow-2xl"
            >
              <div className="border border-[#1E3323]/15 p-1">
                <img
                  src={active.src}
                  alt={active.caption}
                  className={`w-full object-cover ${
                    active.story ? "max-h-[50vh]" : "max-h-[62vh]"
                  }`}
                />
              </div>
              <figcaption className="flex items-end justify-between px-2 pb-2 pt-4">
                <div>
                  <p className="font-serif text-xl text-[#1E3323] sm:text-2xl">
                    {active.caption}
                  </p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#767970]">
                    {active.spec}
                  </p>
                </div>
                <p className="font-mono text-xs text-[#767970]">
                  {String(active.index + 1).padStart(2, "0")} /{" "}
                  {String(IMAGES.length).padStart(2, "0")}
                </p>
              </figcaption>
              {active.story && (
                <div className="border-t border-[#1E3323]/10 px-2 pb-2 pt-3">
                  <p
                    data-testid="gallery-spotlight-story"
                    className="max-h-36 overflow-y-auto pr-2 font-sans text-sm leading-relaxed text-[#1E3323]/80 sm:text-base"
                  >
                    {active.story}
                  </p>
                </div>
              )}
              <button
                data-testid="gallery-spotlight-close"
                onClick={() => setActive(null)}
                className="absolute -right-4 -top-4 rounded-full bg-[#1E3323] p-3 text-[#F7F4EF] shadow-lg transition-colors hover:bg-[#C8634B]"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
